import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import shallow from 'zustand/shallow';
import { IPackagePrice, ISubscribedDevice } from '@root/types/package';
import useCreateOrder from './useCreateOrder';
import Modal from '@root/components/modal';
import Toast from '@root/components/toast';
import { useDefaultPackageState, useUnBindDevicesState } from '@root/state';
import { UNLIMITED_PACKAGE_NUMBER } from '@root/constant';
import { useTranslation } from 'react-i18next';
import { loggerOperatorOnTrack } from '@root/logger/loggerOperator';
import { getQueryString } from '@root/shared';

const useApplePaySelectDevices = (): {
  payDisabled: boolean;
  priceDetail: IPackagePrice;
  selected: string[];
  maxDevNum?: number;
  paying?: boolean;
  isUnlimited?: boolean;
  bindApplePayDevices: ISubscribedDevice[];
  onSelected: (vs: any[]) => void;
  onClickPay: () => void;
} => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedPrice, curPackage, subscribedInfoWithUnit } =
    useDefaultPackageState(
      (state) => ({
        curPackage: state.packageData,
        subscribedInfoWithUnit: state.subscribedInfo,
        selectedPrice: state.selectedPrice,
      }),
      shallow,
    );
  const unBindDevices = useUnBindDevicesState((state) => state.list);
  const { paying, onCreateOrder } = useCreateOrder();
  const [selected, setSelected] = useState<string[]>([]);
  // 单台设备需要实时计算目前要下单哪个套餐
  const [priceDetail, setPriceDetail] = useState<IPackagePrice>(
    {} as IPackagePrice,
  );
  const sensorOriginal = getQueryString('sensor');

  // 当前在上一页选中的是年订阅还是月订阅，后续在计算对应选中设备的套餐时需要根据 单位 来区分
  const curSelectedUnit = selectedPrice.planUnit || 'month';

  // 根据选中的套餐是年还是月获取对应的
  const subscribedInfo = useMemo(() => {
    const _info = subscribedInfoWithUnit[curSelectedUnit] || {};
    return {
      ..._info,
      unlimitedPackageId:
        _info.unlimitedPackageId || subscribedInfoWithUnit.unlimitedPackageId,
    };
  }, [curSelectedUnit, subscribedInfoWithUnit]);

  // 神策数据上报
  const sensorTrack = useCallback(
    (params = {}) => {
      if (!sensorOriginal) {
        return;
      }
      loggerOperatorOnTrack('CloudServiceClickPurchase', {
        original: sensorOriginal,
        pay_mode: 'Apple Pay',
        ...params,
      });
    },
    [sensorOriginal],
  );

  // 是否是无限套餐
  const isUnlimited = selectedPrice.buyNum === UNLIMITED_PACKAGE_NUMBER;
  // 是否有配置无限套餐
  // 超过最大数量时提示要用
  const hasUnlimitedPackage = useMemo(() => {
    return (
      isUnlimited ||
      !!curPackage.goodsPriceList?.find(
        (i) => i.buyNum === UNLIMITED_PACKAGE_NUMBER,
      )
    );
  }, [curPackage.goodsPriceList, isUnlimited]);

  // 最大支持的设备数量，需要根据上个页面选择的是年还是月来判断当前Apple Pay支持的最大数量
  const applePayMaxDevNum = useMemo((): number => {
    if (!curPackage.goodsId) {
      return 0;
    }
    return (
      curPackage.maxDevNum *
      curPackage.goodsPriceList.filter(
        (i) =>
          i.planUnit === curSelectedUnit &&
          i.buyNum !== UNLIMITED_PACKAGE_NUMBER,
      ).length
    );
  }, [curPackage, curSelectedUnit]);

  // 已订阅过的 Apple Pay 支持最大设备数
  // Apple Pay 不允许订阅相同等级的套餐，所以需要占位，告诉用户你已经买过两个设备的套餐了
  const subscribedMaxDevNum = subscribedInfo.maxDevNum || 0;

  // 已购买 Apple Pay 套餐的设备，在下一次购买时需要把当前已经买的列上，一起算最大数量
  const bindApplePayDevices: ISubscribedDevice[] = useMemo(() => {
    // 已购买无限套餐 或 当前无Apple Pay套餐可绑定的设备数量
    if (
      isUnlimited ||
      subscribedInfo.hasApplePayUnlimited ||
      !subscribedMaxDevNum
    ) {
      return [];
    }

    const bindDevices = subscribedInfo.deviceList || [];

    // 没绑定设备的设备占位
    // apple 不允许订阅相同的套餐，所以即使用户没绑定 也要把设备数量的占位展示出来
    const _devicesPlaceholder = [];
    const phNumber = subscribedMaxDevNum - bindDevices.length;

    for (let index = 0; index < phNumber; index++) {
      _devicesPlaceholder.push({
        id: `device_${index}`,
        name: t('no_device_assigned'),
      });
    }

    return bindDevices.concat(_devicesPlaceholder);
  }, [
    isUnlimited,
    subscribedInfo.deviceList,
    subscribedInfo.hasApplePayUnlimited,
    subscribedMaxDevNum,
    t,
  ]);

  // 单个设备套餐时要实时更新对应的Apple Pay套餐
  // 用户每次选择设备对应的都是不同的套餐
  const updateSingleDetail = useCallback(() => {
    const { maxDevNum = 1, goodsPriceList } = curPackage;

    if (!goodsPriceList) {
      return;
    }

    const len = selected.length + subscribedMaxDevNum || 1;

    const num = Math.ceil(len / maxDevNum);
    // 需要根据当前是年还是月来查询当前的套餐
    const _plan =
      goodsPriceList.find(
        (i) => i.planUnit === curSelectedUnit && i.buyNum === num,
      ) || {};

    setPriceDetail(_plan as IPackagePrice);
  }, [curPackage, curSelectedUnit, selected.length, subscribedMaxDevNum]);

  const onSelected = useCallback(
    (vs: any[]) => {
      if (isUnlimited) {
        setSelected(vs);
        return;
      }

      const len = vs.length + subscribedMaxDevNum;

      // 非无限套餐要判断数量是否超过Apple Pay的订阅最大数量
      if (applePayMaxDevNum && len > applePayMaxDevNum) {
        if (hasUnlimitedPackage) {
          Modal.confirm({
            footerColumn: true,
            content: t('recommended_unlimited_device'),
            confirm: t('select_unlimited_package'),
            cancel: t('next_time'),
            onConfirm: () => {
              navigate(`/applePay?defaultPriceType=1&unit=${curSelectedUnit}`, {
                replace: true,
              });
            },
          });
        } else {
          Toast.info(
            applePayMaxDevNum > 1
              ? t('select_max_tips', { maxLength: applePayMaxDevNum })
              : t('select_max_one_tips', { maxLength: applePayMaxDevNum }),
          );
        }
        return;
      }

      setSelected(vs);
    },
    [
      applePayMaxDevNum,
      curSelectedUnit,
      hasUnlimitedPackage,
      isUnlimited,
      navigate,
      subscribedMaxDevNum,
      t,
    ],
  );

  // 已订阅过未选择新的套餐不能购买
  const payDisabled = useMemo(() => {
    if (isUnlimited || !subscribedMaxDevNum) {
      return false;
    }
    return !selected.length;
  }, [isUnlimited, selected.length, subscribedMaxDevNum]);

  const onClickPay = useCallback(() => {
    if (payDisabled) {
      return;
    }

    // 升级套餐需要把已绑定在Apple Pay套餐的设备回传
    const subscribedIds = subscribedInfo.deviceList?.map((i) => i.id) || [];
    const deviceIds = selected.concat(subscribedIds);

    const sensorParams = {
      select_ids: deviceIds.toString(),
      price: `${priceDetail.price}`,
    };

    if (!selected.length) {
      Modal.confirm({
        footerColumn: true,
        content: t('plan_detail_unselect_tips'),
        confirm: t('unselected_continue'),
        cancel: t('cancel'),
        onConfirm: () => {
          onCreateOrder({
            packageInfo: curPackage,
            priceInfo: priceDetail,
            deviceIds,
          });
          sensorTrack(sensorParams);
        },
      });
      return;
    }

    onCreateOrder({
      packageInfo: curPackage,
      priceInfo: priceDetail,
      deviceIds,
    });

    sensorTrack(sensorParams);
  }, [
    curPackage,
    onCreateOrder,
    payDisabled,
    priceDetail,
    selected,
    sensorTrack,
    subscribedInfo.deviceList,
    t,
  ]);

  useEffect(() => {
    if (!curPackage.goodsId) {
      navigate(-1);
    }
  }, [curPackage, navigate]);

  useEffect(() => {
    if (isUnlimited) {
      setSelected((v) => (v.length ? v : unBindDevices.map((i) => i.deviceId)));
    }
  }, [isUnlimited, unBindDevices]);

  // 单个套餐更新价格属性
  useEffect(() => {
    if (!isUnlimited) {
      updateSingleDetail();
    }
  }, [isUnlimited, updateSingleDetail]);

  // 无限套餐更新价格属性
  // 无限套餐只有一个 直接就是上个页面选中的套餐
  useEffect(() => {
    if (isUnlimited && !priceDetail.goodsPriceId) {
      setPriceDetail(selectedPrice);
    }
  }, [isUnlimited, priceDetail.goodsPriceId, selectedPrice]);

  return {
    payDisabled,
    priceDetail,
    selected,
    paying,
    isUnlimited,
    bindApplePayDevices,
    onSelected,
    onClickPay,
  };
};

export default useApplePaySelectDevices;
